import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Loader2, ArrowLeft, Upload } from 'lucide-react';
import { fetchApps, createApp, updateApp, bulkCreate } from '../../utils/adminApi';
import { uploadImage, resolveImage } from '../../utils/imageHelper';

const slugify = (name) =>
  name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const emptyRow = () => ({
  slug: '', name: '', image: '', bonus: 41, minWithdraw: 100,
  downloadLink: '', telegramLink: '', listType: 'main', tag: '',
  customTitle: '', customMetaDescription: '', customAbout: '', customHindiAbout: '', customHowTo: '',
  customFaqs: [],
});

const AppEdit = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [mode, setMode] = useState('single'); // single | bulk1 | bulk5 | bulk10
  const [rows, setRows] = useState([emptyRow()]);

  useEffect(() => {
    if (!isEdit) return;
    fetchApps().then((all) => {
      const found = all.find((a) => a.id === id);
      if (found) setRows([{
        slug: found.slug, name: found.name, image: found.image,
        bonus: found.bonus, minWithdraw: found.minWithdraw,
        downloadLink: found.downloadLink || '', telegramLink: found.telegramLink || '',
        listType: found.listType, tag: found.tag || '',
        customTitle: found.customTitle || '',
        customMetaDescription: found.customMetaDescription || '',
        customAbout: found.customAbout || '',
        customHindiAbout: found.customHindiAbout || '',
        customHowTo: found.customHowTo || '',
        customFaqs: found.customFaqs || [],
        _id: found.id,
      }]);
    });
  }, [id, isEdit]);

  const setModeAndRows = (m) => {
    setMode(m);
    const count = m === 'single' || m === 'bulk1' ? 1 : m === 'bulk5' ? 5 : 10;
    setRows(Array.from({ length: count }, () => emptyRow()));
  };

  const updateRow = (idx, key, val) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      if (key === 'name' && !next[idx].slug) next[idx].slug = slugify(val);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setMsg(''); setSaving(true);
    try {
      if (isEdit) {
        const r = rows[0];
        await updateApp(r._id, {
          slug: r.slug, name: r.name, image: r.image,
          bonus: parseInt(r.bonus, 10), minWithdraw: parseInt(r.minWithdraw, 10),
          downloadLink: r.downloadLink, telegramLink: r.telegramLink,
          listType: r.listType, tag: r.tag,
          customTitle: r.customTitle || '',
          customMetaDescription: r.customMetaDescription || '',
          customAbout: r.customAbout || '',
          customHindiAbout: r.customHindiAbout || '',
          customHowTo: r.customHowTo || '',
          customFaqs: (r.customFaqs || []).filter((f) => (f.q || '').trim() && (f.a || '').trim()),
        });
        setMsg('App updated successfully.');
        setTimeout(() => navigate('/wp-admin/apps'), 800);
      } else {
        const valid = rows.filter((r) => r.name && r.slug && r.image);
        if (valid.length === 0) { setError('Fill at least one row (name, slug, image).'); setSaving(false); return; }
        if (valid.length === 1) {
          await createApp({
            ...valid[0],
            bonus: parseInt(valid[0].bonus, 10),
            minWithdraw: parseInt(valid[0].minWithdraw, 10),
          });
        } else {
          await bulkCreate(valid.map((r) => ({
            ...r,
            bonus: parseInt(r.bonus, 10),
            minWithdraw: parseInt(r.minWithdraw, 10),
          })));
        }
        setMsg(`${valid.length} app(s) added successfully.`);
        setTimeout(() => navigate('/wp-admin/apps'), 800);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button onClick={() => navigate('/wp-admin/apps')} className="inline-flex items-center gap-1 text-sm text-[#2271b1] hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to Apps
      </button>
      <h1 className="text-2xl font-semibold text-gray-900 mb-4">{isEdit ? 'Edit App' : 'Add New App'}</h1>

      {!isEdit && (
        <div className="mb-4 bg-white border border-gray-200 rounded shadow-sm p-3">
          <div className="text-sm font-medium text-gray-800 mb-2">How many apps to add?</div>
          <div className="flex flex-wrap gap-2">
            {[
              { k: 'single', label: '1 App' },
              { k: 'bulk5', label: '5 Apps' },
              { k: 'bulk10', label: '10 Apps' },
            ].map((o) => (
              <button
                key={o.k}
                type="button"
                onClick={() => setModeAndRows(o.k)}
                className={`px-3 py-1.5 text-sm rounded border ${mode === o.k ? 'bg-[#2271b1] text-white border-[#2271b1]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {msg && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{msg}</div>}

      <form onSubmit={submit} className="space-y-4">
        {rows.map((r, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded shadow-sm p-4">
            {rows.length > 1 && <div className="text-xs font-semibold text-gray-500 mb-2">App #{idx + 1}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">App Name *</label>
                <input value={r.name} onChange={(e) => updateRow(idx, 'name', e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Slug (URL) *</label>
                <input value={r.slug} onChange={(e) => updateRow(idx, 'slug', e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">App Image *</label>
                <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  {r.image && (
                    <img src={resolveImage(r.image)} alt="preview" className="w-16 h-16 rounded border border-gray-200 object-cover" />
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      value={r.image}
                      onChange={(e) => updateRow(idx, 'image', e.target.value)}
                      placeholder="Paste image URL or upload below"
                      className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <label className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2271b1] hover:text-[#135e96] cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Upload image from your device
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          try {
                            const url = await uploadImage(f);
                            updateRow(idx, 'image', url);
                          } catch (err) {
                            alert('Upload failed. Please try again.');
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Welcome Bonus (₹)</label>
                <input type="number" value={r.bonus} onChange={(e) => updateRow(idx, 'bonus', e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Min Withdraw (₹)</label>
                <input type="number" value={r.minWithdraw} onChange={(e) => updateRow(idx, 'minWithdraw', e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Download Link</label>
                <input value={r.downloadLink} onChange={(e) => updateRow(idx, 'downloadLink', e.target.value)} placeholder="https://..." className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Telegram Link</label>
                <input value={r.telegramLink} onChange={(e) => updateRow(idx, 'telegramLink', e.target.value)} placeholder="https://t.me/..." className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">List Type</label>
                <select value={r.listType} onChange={(e) => updateRow(idx, 'listType', e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="top">Top Apps (top 3)</option>
                  <option value="main">Main List</option>
                  <option value="latest">Latest Yono Games</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tag (e.g. New)</label>
                <input value={r.tag} onChange={(e) => updateRow(idx, 'tag', e.target.value)} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            {/* Custom content overrides - only show for single/edit */}
            {(isEdit || rows.length === 1) && (
              <details className="mt-4 border-t border-gray-200 pt-3">
                <summary className="cursor-pointer text-sm font-semibold text-[#2271b1] hover:text-[#135e96]">
                  ✏️ Edit Content & SEO (override auto-generated text)
                </summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Custom Meta Title (SEO)</label>
                    <input value={r.customTitle} onChange={(e) => updateRow(idx, 'customTitle', e.target.value)} placeholder="Leave blank to auto-generate" className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Custom Meta Description</label>
                    <textarea value={r.customMetaDescription} onChange={(e) => updateRow(idx, 'customMetaDescription', e.target.value)} placeholder="Leave blank to auto-generate" rows={2} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Custom About Section (English)</label>
                    <textarea value={r.customAbout} onChange={(e) => updateRow(idx, 'customAbout', e.target.value)} placeholder="Write your own About paragraph — leave blank to auto-generate" rows={5} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Custom Hindi About Section</label>
                    <textarea value={r.customHindiAbout} onChange={(e) => updateRow(idx, 'customHindiAbout', e.target.value)} placeholder="अपनी हिंदी जानकारी लिखें — खाली छोड़ने पर auto-generate होगा" rows={5} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Custom How-to-Download Text</label>
                    <textarea value={r.customHowTo} onChange={(e) => updateRow(idx, 'customHowTo', e.target.value)} placeholder="Leave blank to hide this section" rows={3} className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>

                  {/* FAQ editor */}
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-semibold text-gray-800">FAQs (Questions & Answers)</label>
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...(r.customFaqs || []), { q: '', a: '' }];
                          updateRow(idx, 'customFaqs', next);
                        }}
                        className="text-xs font-medium text-[#2271b1] hover:text-[#135e96]"
                      >
                        + Add FAQ
                      </button>
                    </div>
                    {(!r.customFaqs || r.customFaqs.length === 0) && (
                      <p className="text-xs text-gray-500 italic">No FAQs added. Click "+ Add FAQ" to write your own.</p>
                    )}
                    <div className="space-y-2">
                      {(r.customFaqs || []).map((f, fIdx) => (
                        <div key={fIdx} className="border border-gray-200 rounded p-2 bg-gray-50">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-600">FAQ #{fIdx + 1}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const next = (r.customFaqs || []).filter((_, i) => i !== fIdx);
                                updateRow(idx, 'customFaqs', next);
                              }}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Remove
                            </button>
                          </div>
                          <input
                            value={f.q}
                            onChange={(e) => {
                              const next = [...r.customFaqs];
                              next[fIdx] = { ...next[fIdx], q: e.target.value };
                              updateRow(idx, 'customFaqs', next);
                            }}
                            placeholder="Question"
                            className="w-full px-2 py-1 mb-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <textarea
                            value={f.a}
                            onChange={(e) => {
                              const next = [...r.customFaqs];
                              next[fIdx] = { ...next[fIdx], a: e.target.value };
                              updateRow(idx, 'customFaqs', next);
                            }}
                            placeholder="Answer"
                            rows={2}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">
                    <strong>Tip:</strong> All content sections stay hidden until you fill them in. Google will see only what you write — no AI-style filler text.
                  </p>
                </div>
              </details>
            )}
          </div>
        ))}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-[#2271b1] hover:bg-[#135e96] text-white text-sm font-medium px-4 py-2 rounded transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? 'Update App' : `Save ${rows.length > 1 ? rows.length + ' Apps' : 'App'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppEdit;
